// Port of StationDetailView.swift: the body of the station detail sheet.
// The surrounding DetailShell supplies the title and Done button.
import { useQuery } from '@tanstack/react-query';
import type { JSX, ReactNode } from 'react';
import { fetchTidePredictions } from '../../api/coops';
import { Button, Card, MetaRow, Pill, Spinner, StatusMessage } from '../../components/ui/index';
import { formatWeekdayTime } from '../../lib/format';
import type { Station, TideExtreme } from '../../types/stations';
import { TideChart } from './TideChart';
import { formatDegrees, formatFeet, selectNextTides } from './tideModel';
import './station-detail.css';

const SOURCE_LABEL = 'NOAA CO-OPS Tides & Currents';

/** StationDetailViewModel.load: the 3-day hourly curve plus high/low events. */
function useTidePredictionsQuery(stationId: string) {
  return useQuery({
    queryKey: ['tide-predictions', stationId],
    queryFn: ({ signal }) => fetchTidePredictions(stationId, 3, signal),
    staleTime: 5 * 60 * 1000,
  });
}

export function StationDetailPanel({ station }: { station: Station }): JSX.Element {
  const query = useTidePredictionsQuery(station.id);

  return (
    <div className="sd-panel">
      <Card className="sd-header">
        <h3 className="sd-name">{station.name}</h3>
        <div className="sd-pills">
          <Pill color="var(--text-2)" outlined title="Station ID">
            <span className="sd-pill-glyph" aria-hidden="true">
              #
            </span>
            <span className="sd-pill-text">{station.id}</span>
          </Pill>
          {station.state ? (
            <Pill color="var(--text-2)" outlined title="State">
              <PinGlyph />
              <span className="sd-pill-text">{station.state}</span>
            </Pill>
          ) : null}
        </div>
      </Card>

      <Card className="sd-card">
        <div className="sd-chart-head">
          <h3 className="sd-chart-title">Tide Predictions</h3>
          <span className="sd-chart-unit">ft (MLLW)</span>
        </div>
        <TideSection station={station} query={query} />
      </Card>

      <Card title="Location" className="sd-card">
        <div className="sd-rows">
          <MetaRow label="Latitude" value={formatDegrees(station.lat)} />
          <MetaRow label="Longitude" value={formatDegrees(station.lng)} />
          {station.state ? <MetaRow label="State" value={station.state} /> : null}
          {station.timezone ? <MetaRow label="Time Zone" value={station.timezone} /> : null}
        </div>
      </Card>

      <Card title="Characteristics" className="sd-card">
        <div className="sd-rows">
          {station.tideType ? <MetaRow label="Tide Type" value={station.tideType} /> : null}
          <CapabilityRow label="Tidal" isOn={station.isTidal} />
          <CapabilityRow label="Great Lakes" isOn={station.isGreatLakes} />
          <CapabilityRow label="Tide Forecast" isOn={station.hasForecast} />
          <CapabilityRow label="Storm Surge" isOn={station.hasStormSurge} />
        </div>
      </Card>

      <Card title="Station Details" className="sd-card sd-footer">
        <div className="sd-rows">
          <MetaRow label="Station ID" value={station.id} />
          {station.shefCode ? <MetaRow label="SHEF Code" value={station.shefCode} /> : null}
          {station.affiliations ? <MetaRow label="Affiliations" value={station.affiliations} /> : null}
          <MetaRow label="Source" value={SOURCE_LABEL} />
        </div>
      </Card>
    </div>
  );
}

// MARK: - Tide Predictions

function TideSection({ station, query }: { station: Station; query: ReturnType<typeof useTidePredictionsQuery> }) {
  // A retry after an error keeps status 'error' while fetching, so treat that as loading too.
  if (query.isPending || (query.isError && query.isFetching)) {
    return (
      <div className="sd-chart-state">
        <Spinner label="Loading tide predictions…" />
      </div>
    );
  }
  if (query.isError) {
    const message = query.error instanceof Error ? query.error.message : String(query.error);
    return (
      <div className="sd-chart-state">
        <StatusMessage
          action={
            <Button onClick={() => void query.refetch()} variant="default">
              Retry
            </Button>
          }
        >
          <span className="sd-placeholder-icon" aria-hidden="true">
            <WarningGlyph />
          </span>
          <div className="sd-placeholder-text">
            Unable to load tide predictions.
            <br />
            {message}
          </div>
        </StatusMessage>
      </div>
    );
  }
  const { curve, extremes } = query.data;
  if (curve.length === 0) {
    return (
      <div className="sd-chart-state">
        <StatusMessage>
          <span className="sd-placeholder-icon" aria-hidden="true">
            <NoWavesGlyph />
          </span>
          <div className="sd-placeholder-text">No tide predictions available for this station.</div>
        </StatusMessage>
      </div>
    );
  }
  const upcoming = selectNextTides(extremes, new Date());
  return (
    <>
      <TideChart stationName={station.name} curve={curve} extremes={extremes} />
      {upcoming.length > 0 ? <NextTides tides={upcoming} /> : null}
      {extremes.length > 0 ? <ExtremesTable extremes={extremes} /> : null}
    </>
  );
}

/** StationDetailView.nextTideSummary: arrow, High/Low, weekday + time, height. */
function NextTides({ tides }: { tides: TideExtreme[] }) {
  return (
    <ul className="sd-next" aria-label="Next tides">
      {tides.map((tide) => (
        <li key={`${tide.t}-${tide.isHigh ? 'H' : 'L'}`} className="sd-next-row">
          <ArrowGlyph up={tide.isHigh} />
          <span className="sd-next-kind">{tide.isHigh ? 'High' : 'Low'}</span>
          <span className="sd-next-time">{formatWeekdayTime(tide.date)}</span>
          <span className="sd-next-value">{formatFeet(tide.value)} ft</span>
        </li>
      ))}
    </ul>
  );
}

/** Table-view twin of the chart: every predicted high and low. */
function ExtremesTable({ extremes }: { extremes: TideExtreme[] }) {
  return (
    <details className="sd-table">
      <summary>All highs and lows</summary>
      <table>
        <thead>
          <tr>
            <th scope="col">Time</th>
            <th scope="col">Tide</th>
            <th scope="col" className="sd-num">
              Height (ft)
            </th>
          </tr>
        </thead>
        <tbody>
          {extremes.map((e) => (
            <tr key={`${e.t}-${e.isHigh ? 'H' : 'L'}`}>
              <td>{formatWeekdayTime(e.date)}</td>
              <td>{e.isHigh ? 'High' : 'Low'}</td>
              <td className="sd-num">{formatFeet(e.value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

// MARK: - Building blocks

/** StationDetailView.capabilityRow: check/x glyph, label, Yes/No. */
function CapabilityRow({ label, isOn }: { label: string; isOn: boolean }) {
  return (
    <div className="sd-cap-row">
      <span className={`sd-cap-icon ${isOn ? 'is-on' : ''}`} aria-hidden="true">
        {isOn ? <CheckCircleGlyph /> : <XCircleGlyph />}
      </span>
      <span className="sd-cap-label">{label}</span>
      <span className={`sd-cap-value ${isOn ? 'is-on' : ''}`}>{isOn ? 'Yes' : 'No'}</span>
    </div>
  );
}

// Inline stand-ins for the SF Symbols the iOS view uses.

function Glyph({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    <svg className={`sd-glyph ${className}`} viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false">
      {children}
    </svg>
  );
}

function PinGlyph() {
  return (
    <Glyph className="sd-pill-glyph">
      <path d="M8 1.5a4 4 0 0 1 4 4c0 2.6-2.7 5.6-3.6 6.6a.5.5 0 0 1-.8 0C6.7 11.1 4 8.1 4 5.5a4 4 0 0 1 4-4z" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <circle cx="8" cy="5.5" r="1.4" fill="currentColor" />
      <path d="M4.5 13.5h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </Glyph>
  );
}

function ArrowGlyph({ up }: { up: boolean }) {
  return (
    <Glyph className={`sd-arrow ${up ? 'sd-arrow-high' : 'sd-arrow-low'}`}>
      {up ? (
        <path d="M8 13V3.5M4 7.5 8 3.5l4 4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      ) : (
        <path d="M8 3v9.5M4 8.5l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      )}
    </Glyph>
  );
}

function CheckCircleGlyph() {
  return (
    <Glyph>
      <circle cx="8" cy="8" r="7" fill="currentColor" />
      <path d="M4.8 8.2l2.1 2.1 4.3-4.6" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </Glyph>
  );
}

function XCircleGlyph() {
  return (
    <Glyph>
      <circle cx="8" cy="8" r="6.5" fill="none" stroke="currentColor" strokeWidth="1.3" />
      <path d="M5.6 5.6l4.8 4.8M10.4 5.6l-4.8 4.8" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </Glyph>
  );
}

function WarningGlyph() {
  return (
    <svg className="sd-glyph sd-glyph-lg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path d="M12 3.5 21.5 20H2.5L12 3.5z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M12 9.5v5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="17" r="1.1" fill="currentColor" />
    </svg>
  );
}

function NoWavesGlyph() {
  return (
    <svg className="sd-glyph sd-glyph-lg" viewBox="0 0 24 24" width="28" height="28" aria-hidden="true" focusable="false">
      <path d="M3 9c2 0 2 1.5 4 1.5S9 9 11 9s2 1.5 4 1.5S17 9 19 9s2 1.5 2 1.5M3 15c2 0 2 1.5 4 1.5s2-1.5 4-1.5 2 1.5 4 1.5 2-1.5 4-1.5 2 1.5 2 1.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M4 20 20 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
